import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { DateUtils } from '../../../../../common/helpers/date-utils';
import { loadConvertedFiles } from '../../../state/admin.actions';
import { Store } from '@ngrx/store';
import { selectConvertedFiles } from '../../../state/admin.selector';
import { AsyncPipe, NgClass } from '@angular/common';
import { PaginationComponent } from '../../components/pagination/pagination.component';
import { map, take } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { MultiSelectDropdownComponent } from '../../components/multi-select-dropdown/multi-select-dropdown.component';
import { NgxDaterangepickerBootstrapDirective } from 'ngx-daterangepicker-bootstrap';
import moment from 'moment';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatepickerUtils } from '../../../../../common/helpers/date-picker-utils';
import { CompanyService } from '../../../data/services/company.service';
import { environment } from '../../../../../../environments/environment';

@Component({
  selector: 'app-converted-files',
  standalone: true,
  imports: [
    AsyncPipe,
    NgClass,
    PaginationComponent,
    FormsModule,
    MultiSelectDropdownComponent,
    NgxDaterangepickerBootstrapDirective,
    TranslatePipe,
  ],
  templateUrl: './converted-files.component.html',
  styleUrl: './converted-files.component.scss',
})
export class ConvertedFilesComponent implements OnInit {
  @ViewChild(NgxDaterangepickerBootstrapDirective, { static: false })
  pickerDirective!: NgxDaterangepickerBootstrapDirective;
  selectedFileType = '';
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  fileTypeOptions = [
    { label: 'Gerber', value: 'gerber' },
    { label: 'Lectra', value: 'lectra' },
  ];
  companyOptions: { label: string; value: string }[] = [];
  selectedCompanies: string[] = [];
  rangeDates: Date[] = [];
  selectedDates:
    | { startDate: moment.Moment; endDate: moment.Moment }
    | undefined;
  protected readonly dateUtils = inject(DateUtils);
  protected datePickerUtils = inject(DatepickerUtils);
  protected translate = inject(TranslateService);
  protected companyService = inject(CompanyService);
  protected datepickerLocale = {};
  protected readonly environment = environment;
  private store = inject(Store);
  protected convertedFiles$ = this.store.select(selectConvertedFiles);
  isFilterApplied$ = this.store
    .select(selectConvertedFiles)
    .pipe(
      map(
        (cf) =>
          !!cf.fileType ||
          !!cf.startDate ||
          !!cf.endDate ||
          !!cf.companyIds?.length,
      ),
    );

  ngOnInit(): void {
    this.updateLocale();

    this.translate.onLangChange.subscribe(() => {
      this.updateLocale();
    });

    this.store
      .select(selectConvertedFiles)
      .pipe(take(1))
      .subscribe((convertedFiles) => {
        const { startDate, endDate } = convertedFiles ?? {};
        if (startDate && endDate) {
          this.selectedDates = {
            startDate: moment(startDate),
            endDate: moment(endDate),
          };
        }
      });
    this.store
      .select(selectConvertedFiles)
      .pipe(take(1))
      .subscribe((cf) => {
        this.selectedFileType = cf.fileType ?? '';
        this.rangeDates =
          cf.startDate && cf.endDate
            ? [new Date(cf.startDate), new Date(cf.endDate)]
            : [];
        this.store.dispatch(
          loadConvertedFiles({
            page: cf.page || 1,
            pageSize: cf.pageSize || 10,
            search: cf.search,
            fileType: cf.fileType,
            sortColumn: this.sortColumn,
            sortDirection: this.sortDirection,
            startDate: this.rangeDates[0],
            endDate: this.rangeDates[1],
          }),
        );
      });
  }

  updateLocale() {
    this.datePickerUtils.getDatepickerLocale().subscribe((locale) => {
      this.datepickerLocale = locale;
    });
  }

  onCompanySearch(term: string) {
    if (!term) {
      this.companyOptions = [];
      return;
    }
    this.companyService.searchCompanies(term).subscribe((res) => {
      const companies = res.companies ?? [];
      this.companyOptions = companies.map((c) => ({
        label: `${c.lmCode} - ${c.name}`,
        value: c.id,
      }));
    });
  }

  onCompanyChange(selected: string[] | string) {
    this.selectedCompanies = Array.isArray(selected) ? selected : [selected];
    this.store
      .select(selectConvertedFiles)
      .pipe(take(1))
      .subscribe((cf) => {
        this.store.dispatch(
          loadConvertedFiles({
            page: 1,
            pageSize: cf.pageSize,
            search: cf.search,
            fileType: cf.fileType,
            sortColumn: this.sortColumn,
            sortDirection: this.sortDirection,
            startDate: cf.startDate,
            endDate: cf.endDate,
            companyIds: this.selectedCompanies,
          }),
        );
      });
  }

  onSearchChange(value: string) {
    this.store
      .select(selectConvertedFiles)
      .pipe(take(1))
      .subscribe((cf) => {
        this.store.dispatch(
          loadConvertedFiles({
            page: 1,
            pageSize: cf.pageSize,
            search: value,
            startDate: cf.startDate,
            endDate: cf.endDate,
          }),
        );
      });
  }

  onDateRangeChange(selected: any) {
    if (!selected?.startDate || !selected?.endDate) return;
    const startDate = selected.startDate.startOf('day').toDate();
    const endDate = selected.endDate.endOf('day').toDate();
    this.store
      .select(selectConvertedFiles)
      .pipe(take(1))
      .subscribe((cf) => {
        this.store.dispatch(
          loadConvertedFiles({
            page: 1,
            pageSize: cf.pageSize,
            search: cf.search,
            fileType: cf.fileType,
            sortColumn: this.sortColumn,
            sortDirection: this.sortDirection,
            startDate,
            endDate,
          }),
        );
      });
  }

  onFileTypeChange(fileType: string | string[]) {
    const typeValue = Array.isArray(fileType) ? fileType[0] || '' : fileType;
    this.store
      .select(selectConvertedFiles)
      .pipe(take(1))
      .subscribe((cf) => {
        this.store.dispatch(
          loadConvertedFiles({
            page: 1,
            pageSize: cf.pageSize,
            search: cf.search,
            fileType: typeValue,
            startDate: cf.startDate,
            endDate: cf.endDate,
          }),
        );
      });
  }

  onPageChange(page: number): void {
    this.store
      .select(selectConvertedFiles)
      .pipe(take(1))
      .subscribe((cf) => {
        this.store.dispatch(
          loadConvertedFiles({
            page,
            pageSize: cf.pageSize,
            search: cf.search,
            fileType: cf.fileType,
            sortColumn: this.sortColumn,
            sortDirection: this.sortDirection,
            startDate: cf.startDate,
            endDate: cf.endDate,
          }),
        );
      });
  }

  onPageSizeChange(newSize: number): void {
    this.store
      .select(selectConvertedFiles)
      .pipe(take(1))
      .subscribe((cf) => {
        this.store.dispatch(
          loadConvertedFiles({
            page: 1,
            pageSize: newSize,
            search: cf.search,
            fileType: cf.fileType,
            startDate: cf.startDate,
            endDate: cf.endDate,
          }),
        );
      });
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.store
      .select(selectConvertedFiles)
      .pipe(take(1))
      .subscribe((cf) => {
        this.store.dispatch(
          loadConvertedFiles({
            page: 1,
            pageSize: cf.pageSize,
            search: cf.search,
            fileType: cf.fileType,
            sortColumn: this.sortColumn,
            sortDirection: this.sortDirection,
            startDate: cf.startDate,
            endDate: cf.endDate,
          }),
        );
      });
  }

  clearFilters(): void {
    this.selectedFileType = '';
    this.rangeDates = [];
    this.selectedDates = undefined;
    this.sortColumn = '';
    this.sortDirection = 'asc';
    const search = '';
    this.selectedCompanies = [];

    this.store
      .select(selectConvertedFiles)
      .pipe(take(1))
      .subscribe((cf) => {
        this.store.dispatch(
          loadConvertedFiles({
            page: 1,
            pageSize: cf.pageSize,
            search,
            fileType: '',
            sortColumn: '',
            sortDirection: 'asc',
            startDate: undefined,
            endDate: undefined,
            companyIds: undefined,
          }),
        );
      });
  }
}
